import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Layers, Plus, Building2, Trash2, Eye, Loader2, MapPin, ArrowLeft, Compass, Upload, FileText, Calculator, Package, Waves, Leaf, Sun, Umbrella, BookOpen, Shirt, ArrowUpDown, Wine, Flame, Dumbbell, Thermometer, Droplets, PawPrint, Tv, Wifi, Zap, CookingPot, X, Image, Video, Globe, Navigation, Edit3, Send, History, ExternalLink } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PhoneInput from '@/components/PhoneInput';
import CampaignPanel from '@/components/CampaignPanel';
import CopyableId from '@/components/CopyableId';

const STATUS_COLORS = {
  disponible: 'bg-emerald-500/80 hover:bg-emerald-500',
  apartado: 'bg-amber-500/80 hover:bg-amber-500',
  vendido: 'bg-red-500/80 hover:bg-red-500',
  no_disponible: 'bg-gray-600/80 hover:bg-gray-600',
};
const STATUS_LABELS = { disponible: 'Disponible', apartado: 'Apartado', vendido: 'Vendido', no_disponible: 'No Disponible' };
const ORIENTATIONS = ['norte', 'sur', 'este', 'oeste', 'noreste', 'noroeste', 'sureste', 'suroeste'];
const ORI_SHORT = { norte: 'N', sur: 'S', este: 'E', oeste: 'O', noreste: 'NE', noroeste: 'NO', sureste: 'SE', suroeste: 'SO' };

const AMENITY_ICONS = {
  bodega: Package, jacuzzi: Waves, roof_garden: Leaf, balcon: Sun, terraza: Umbrella,
  estudio: BookOpen, walk_in_closet: Shirt, cuarto_lavado: Droplets, doble_altura: ArrowUpDown,
  vestidor: Shirt, bar: Wine, chimenea: Flame, gym_privado: Dumbbell, sauna: Thermometer,
  alberca_privada: Droplets, pet_friendly: PawPrint, sala_tv: Tv, cocina_integral: CookingPot,
  paneles_solares: Zap, sistema_smart: Wifi,
};
const AMENITY_NAMES = {
  bodega: 'Bodega', jacuzzi: 'Jacuzzi', roof_garden: 'Roof Garden', balcon: 'Balcon',
  terraza: 'Terraza', estudio: 'Estudio', walk_in_closet: 'Walk-in Closet',
  cuarto_lavado: 'Cuarto de Lavado', doble_altura: 'Doble Altura', vestidor: 'Vestidor',
  bar: 'Bar / Cava', chimenea: 'Chimenea', gym_privado: 'Gym Privado', sauna: 'Sauna',
  alberca_privada: 'Alberca Privada', pet_friendly: 'Pet Friendly', sala_tv: 'Sala TV',
  cocina_integral: 'Cocina Integral', paneles_solares: 'Paneles Solares', sistema_smart: 'Smart Home',
};

const MX_STATES = [
  { key: 'aguascalientes', label: 'Aguascalientes' },
  { key: 'baja_california', label: 'Baja California' },
  { key: 'baja_california_sur', label: 'Baja California Sur' },
  { key: 'campeche', label: 'Campeche' },
  { key: 'chiapas', label: 'Chiapas' },
  { key: 'chihuahua', label: 'Chihuahua' },
  { key: 'cdmx', label: 'Ciudad de Mexico' },
  { key: 'coahuila', label: 'Coahuila' },
  { key: 'colima', label: 'Colima' },
  { key: 'durango', label: 'Durango' },
  { key: 'estado_de_mexico', label: 'Estado de Mexico' },
  { key: 'guanajuato', label: 'Guanajuato' },
  { key: 'guerrero', label: 'Guerrero' },
  { key: 'hidalgo', label: 'Hidalgo' },
  { key: 'jalisco', label: 'Jalisco' },
  { key: 'michoacan', label: 'Michoacan' },
  { key: 'morelos', label: 'Morelos' },
  { key: 'nayarit', label: 'Nayarit' },
  { key: 'nuevo_leon', label: 'Nuevo Leon' },
  { key: 'oaxaca', label: 'Oaxaca' },
  { key: 'puebla', label: 'Puebla' },
  { key: 'queretaro', label: 'Queretaro' },
  { key: 'quintana_roo', label: 'Quintana Roo' },
  { key: 'san_luis_potosi', label: 'San Luis Potosi' },
  { key: 'sinaloa', label: 'Sinaloa' },
  { key: 'sonora', label: 'Sonora' },
  { key: 'tabasco', label: 'Tabasco' },
  { key: 'tamaulipas', label: 'Tamaulipas' },
  { key: 'tlaxcala', label: 'Tlaxcala' },
  { key: 'veracruz', label: 'Veracruz' },
  { key: 'yucatan', label: 'Yucatan' },
  { key: 'zacatecas', label: 'Zacatecas' },
];

export default function DevelopmentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [devs, setDevs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', type: 'edificio', address: '', city: '', state: '', floors: 5, units_per_floor: 4, orientations: ['norte', 'sur', 'este', 'oeste'], base_price: 0, unit_type: 'departamento', area_m2: 0, bedrooms: 2, bathrooms: 1, currency: 'MXN' });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverPreview, setCoverPreview] = useState('');
  const coverRef = useRef(null);
  const [unitFloorPlanUploading, setUnitFloorPlanUploading] = useState(false);
  const unitPlanRef = useRef(null);
  const [quoteDialog, setQuoteDialog] = useState(false);
  const [quoteUnit, setQuoteUnit] = useState(null);
  const [quoteForm, setQuoteForm] = useState({ down_payment_pct: 20, annual_interest_rate: 12, term_months: 240, notarial_state: '' });
  const [notarialCatalog, setNotarialCatalog] = useState({});
  const [quoteResult, setQuoteResult] = useState(null);
  const [quoteGenerating, setQuoteGenerating] = useState(false);
  const [editFicha, setEditFicha] = useState(false);
  const [fichaForm, setFichaForm] = useState({});
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [brochureUploading, setBrochureUploading] = useState(false);
  const galleryRef = useRef(null);
  const brochureRef = useRef(null);
  const [quotesHistory, setQuotesHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [quoteFlowContext, setQuoteFlowContext] = useState(null);
  const [quoteDelivery, setQuoteDelivery] = useState({ send_whatsapp: true, send_email: true, recipient_phone: '', recipient_email: '' });

  const API = process.env.REACT_APP_BACKEND_URL;
  const resolveUrl = (url) => url ? (url.startsWith('http') ? url : `${API}${url}`) : '';

  const load = useCallback(async () => {
    try { const r = await api.get('/developments'); setDevs(r.data); }
    catch { toast.error('Error al cargar desarrollos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get('/developments/notarial-costs-catalog').then(r => setNotarialCatalog(r.data?.states || {})).catch(() => {});
  }, [load]);

  const uploadFile = async (file, setUploading) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/developments/upload-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return r.data;
    } catch { toast.error('Error al subir archivo'); return null; }
    finally { setUploading(false); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = await uploadFile(file, setCoverUploading);
    if (r) { setCoverPreview(r.url); setForm(f => ({ ...f, cover_image_url: r.url })); toast.success('Imagen cargada'); }
  };

  const uploadGalleryImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setGalleryUploading(true);
    const urls = [];
    for (const file of files) {
      const r = await uploadFile(file, () => {});
      if (r) urls.push(r.url);
    }
    setFichaForm(f => ({ ...f, gallery_images: [...(f.gallery_images || []), ...urls] }));
    setGalleryUploading(false);
    if (urls.length) toast.success(`${urls.length} imagen(es) subida(s)`);
  };

  const uploadBrochure = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = await uploadFile(file, setBrochureUploading);
    if (r) { setFichaForm(f => ({ ...f, brochure_url: r.url })); toast.success('Brochure cargado'); }
  };

  const create = async () => {
    if (!form.name) { toast.error('Nombre requerido'); return; }
    setSaving(true);
    try {
      await api.post('/developments', { ...form, cover_image_url: coverPreview || form.cover_image_url });
      toast.success('Desarrollo creado'); setShowForm(false);
      setForm({ name: '', description: '', type: 'edificio', address: '', city: '', state: '', floors: 5, units_per_floor: 4, orientations: ['norte', 'sur', 'este', 'oeste'], base_price: 0, unit_type: 'departamento', area_m2: 0, bedrooms: 2, bathrooms: 1, currency: 'MXN' });
      setCoverPreview(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminar desarrollo y todas sus unidades?')) return;
    try { await api.delete(`/developments/${id}`); toast.success('Eliminado'); load(); }
    catch { toast.error('Error'); }
  };

  const openDev = async (id) => {
    try { const r = await api.get(`/developments/${id}`); setSelected(r.data); }
    catch { toast.error('Error al cargar desarrollo'); }
  };

  useEffect(() => {
    const oppId = searchParams.get('opp_id');
    const targetStageId = searchParams.get('target_stage_id');
    const quoteFlow = searchParams.get('quote_flow');
    if (!oppId || quoteFlow !== '1') {
      setQuoteFlowContext(null);
      return;
    }

    api.get(`/opportunities/${oppId}/quote-context`).then((response) => {
      const context = {
        ...response.data,
        opportunity_id: oppId,
        target_stage_id: targetStageId || '',
      };
      setQuoteFlowContext(context);
      setQuoteDelivery({
        send_whatsapp: true,
        send_email: true,
        recipient_phone: response.data?.lead?.phone || '',
        recipient_email: response.data?.lead?.email || '',
      });

      const suggestedDev = response.data?.opportunity?.development_id;
      if (suggestedDev) {
        openDev(suggestedDev);
      }
      toast.info('Flujo de cotización: selecciona una unidad en el mapa 2D y envía.');
    }).catch((e) => {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el contexto de cotización');
    });
  }, [searchParams]);

  const updateUnit = async (unitId, updates) => {
    try {
      await api.put(`/developments/${selected.id}/units/${unitId}`, updates);
      toast.success('Unidad actualizada'); openDev(selected.id); setSelectedUnit(null);
    } catch { toast.error('Error al actualizar'); }
  };

  const saveFicha = async () => {
    try {
      await api.put(`/developments/${selected.id}`, fichaForm);
      toast.success('Ficha actualizada'); openDev(selected.id); setEditFicha(false);
    } catch { toast.error('Error al guardar'); }
  };

  const uploadUnitFloorPlan = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = await uploadFile(file, setUnitFloorPlanUploading);
    if (r) { setSelectedUnit(u => ({ ...u, floor_plan_url: r.url })); toast.success('Plano cargado'); }
  };

  const toggleUnitAmenity = (key) => {
    setSelectedUnit(u => ({ ...u, amenities: (u.amenities || []).includes(key) ? u.amenities.filter(a => a !== key) : [...(u.amenities || []), key] }));
  };

  const openQuote = (unit) => {
    setQuoteUnit(unit);
    setQuoteForm({ down_payment_pct: 20, annual_interest_rate: 12, term_months: 240, notarial_state: '' });
    setQuoteResult(null); setQuoteDialog(true);
  };

  const generateQuote = async () => {
    if (!quoteUnit || !selected) return;
    setQuoteGenerating(true);
    try {
      const amenityCosts = {};
      (quoteUnit.amenities || []).forEach(a => { amenityCosts[a] = (quoteUnit.amenity_costs || {})[a] || 0; });
      if (quoteFlowContext?.opportunity_id) {
        const r = await api.post(`/opportunities/${quoteFlowContext.opportunity_id}/development-quote/send`, {
          development_id: selected.id,
          unit_id: quoteUnit.id,
          base_price: quoteUnit.price || 0,
          selected_amenities: quoteUnit.amenities || [],
          amenity_costs: amenityCosts,
          target_stage_id: quoteFlowContext.target_stage_id || undefined,
          ...quoteForm,
          ...quoteDelivery,
        });
        setQuoteResult(r.data?.quote || null);
        toast.success(r.data?.stage_updated ? 'Cotización enviada y oportunidad movida a Cotización' : 'Cotización enviada');
        if (r.data?.stage_updated) {
          navigate('/developments', { replace: true });
          setQuoteFlowContext(null);
        }
      } else {
        const r = await api.post(`/developments/${selected.id}/quotes`, {
          unit_id: quoteUnit.id, base_price: quoteUnit.price || 0,
          selected_amenities: quoteUnit.amenities || [], amenity_costs: amenityCosts,
          ...quoteForm,
        });
        setQuoteResult(r.data);
        toast.success('Cotización generada');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al generar cotización'); }
    finally { setQuoteGenerating(false); }
  };

  const loadQuoteHistory = async (devId) => {
    try { const r = await api.get(`/developments/${devId}/quotes`); setQuotesHistory(r.data); setShowHistory(true); }
    catch { toast.error('Error'); }
  };

  const resendQuote = async (quoteId, email) => {
    try {
      await api.post(`/quotes/${quoteId}/resend`, { send_email: !!email, recipient_email: email });
      toast.success(email ? 'Cotizacion enviada por email' : 'Cotizacion regenerada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al enviar'); }
  };

  const toggleOrientation = (o) => setForm(f => ({ ...f, orientations: f.orientations.includes(o) ? f.orientations.filter(x => x !== o) : [...f.orientations, o] }));

  const notarialInfo = notarialCatalog[quoteForm.notarial_state] || null;

  // ─── QUOTATION RESULT VIEW ───
  if (quoteResult) {
    const q = quoteResult;
    const cur = '$';
    return (
      <div data-testid="quote-result-view">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => { setQuoteResult(null); setQuoteDialog(false); }} className="text-muted-foreground hover:text-white" data-testid="quote-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />Volver al Mapa
          </Button>
        </div>
        <Card className="bg-card/50 backdrop-blur-md border-white/5 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-white font-heading">Cotizacion Simulada</h2>
                <p className="text-xs text-muted-foreground">{q.development_name} - {q.unit_label}</p>
              </div>
              <Badge variant="outline" className="text-amber-400 border-amber-500/20 text-[10px]">SIMULACION</Badge>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-3 mb-6">
              <p className="text-[11px] text-amber-400 font-medium">Esta cotizacion es una simulacion con fines informativos. Los montos, tasas e importes son estimados y no representan una oferta financiera vinculante.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[['Unidad', q.unit_label], ['Piso', q.unit_floor], ['m2', q.unit_area_m2], ['Rec / Banos', `${q.unit_bedrooms} / ${q.unit_bathrooms}`]].map(([l, v]) => (
                <div key={l} className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <p className="text-[10px] text-muted-foreground">{l}</p>
                  <p className="text-sm font-bold text-white">{v}</p>
                </div>
              ))}
            </div>
            {/* Pricing Breakdown */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-white mb-3">Desglose de Precio</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Precio base unidad</span><span className="text-white font-medium">{cur}{q.base_price?.toLocaleString()}</span></div>
                {q.selected_amenities?.map(a => (
                  <div key={a} className="flex justify-between text-xs pl-3"><span className="text-muted-foreground">+ {AMENITY_NAMES[a] || a}</span><span className="text-white">{cur}{(q.amenity_costs?.[a] || 0).toLocaleString()}</span></div>
                ))}
                {q.amenity_total > 0 && <div className="flex justify-between text-xs border-t border-white/5 pt-2"><span className="text-muted-foreground">Total amenidades</span><span className="text-violet-400 font-medium">+{cur}{q.amenity_total.toLocaleString()}</span></div>}
                <div className="flex justify-between text-sm border-t border-white/10 pt-2 font-semibold"><span className="text-white">PRECIO TOTAL INMUEBLE</span><span className="text-emerald-400">{cur}{q.total_price?.toLocaleString()}</span></div>
              </div>
            </div>
            {/* Notarial Costs */}
            {q.notarial_total > 0 && (
              <div className="mb-6 p-4 rounded-lg bg-red-500/[0.03] border border-red-500/10">
                <h3 className="text-sm font-semibold text-red-400 mb-3">Gastos Notariales Estimados ({q.notarial_state?.replace(/_/g, ' ')})</h3>
                <div className="space-y-1.5">
                  {[['ISAI', q.notarial_isai], ['Honorarios Notariales', q.notarial_notario], ['Registro Publico', q.notarial_registro], ['Avaluo', q.notarial_avaluo]].map(([l, v]) => (
                    <div key={l} className="flex justify-between text-xs"><span className="text-muted-foreground">{l}</span><span className="text-white">{cur}{v?.toLocaleString()}</span></div>
                  ))}
                  <div className="flex justify-between text-sm border-t border-red-500/10 pt-2 font-semibold"><span className="text-red-400">Total Gastos Notariales</span><span className="text-red-400">{cur}{q.notarial_total?.toLocaleString()}</span></div>
                </div>
              </div>
            )}
            {/* Financing */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-white mb-3">Esquema de Financiamiento</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[['Enganche', `${q.down_payment_pct}%`, q.down_payment, 'blue'], ['A Financiar', '', q.financed_amount, 'violet'], ['Tasa Anual', `${q.annual_interest_rate}%`, null, 'amber'], ['Plazo', `${q.term_months} meses`, null, 'emerald']].map(([l, sub, v, c]) => (
                  <div key={l} className={`p-3 rounded-lg bg-${c}-500/5 border border-${c}-500/10`}>
                    <p className={`text-[10px] text-${c}-400`}>{l} {sub}</p>
                    <p className="text-sm font-bold text-white">{v !== null ? `${cur}${v?.toLocaleString()}` : sub}</p>
                  </div>
                ))}
              </div>
            </div>
            {/* Monthly Payment */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 mb-6 text-center">
              <p className="text-xs text-muted-foreground mb-1">Pago Mensual Estimado</p>
              <p className="text-3xl font-bold text-emerald-400">{cur}{q.monthly_payment?.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Total: {cur}{q.total_paid?.toLocaleString()} | Intereses: {cur}{q.total_interest?.toLocaleString()}</p>
              {q.notarial_total > 0 && <p className="text-[10px] text-red-400 mt-1">+ Gastos Notariales: {cur}{q.notarial_total?.toLocaleString()}</p>}
            </div>
            {/* Amortization */}
            {q.amortization_preview?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Tabla de Amortizacion (Primeros 12 meses)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead><tr className="text-muted-foreground border-b border-white/5"><th className="text-left py-2 px-2">Mes</th><th className="text-right py-2 px-2">Pago</th><th className="text-right py-2 px-2">Capital</th><th className="text-right py-2 px-2">Interes</th><th className="text-right py-2 px-2">Saldo</th></tr></thead>
                    <tbody>{q.amortization_preview.map(r => (
                      <tr key={r.month} className="border-b border-white/[0.02] hover:bg-white/[0.02]"><td className="py-1.5 px-2 text-white">{r.month}</td><td className="py-1.5 px-2 text-right text-white">{cur}{r.payment?.toLocaleString()}</td><td className="py-1.5 px-2 text-right text-emerald-400">{cur}{r.principal?.toLocaleString()}</td><td className="py-1.5 px-2 text-right text-amber-400">{cur}{r.interest?.toLocaleString()}</td><td className="py-1.5 px-2 text-right text-muted-foreground">{cur}{r.balance?.toLocaleString()}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── 2D MAP + FICHA VIEW ───
  if (selected) {
    const units = selected.units || [];
    const maxFloor = selected.floors || 1;
    const unitsPerFloor = selected.units_per_floor || 1;
    const statusCounts = { disponible: 0, apartado: 0, vendido: 0, no_disponible: 0 };
    units.forEach(u => { if (statusCounts[u.status] !== undefined) statusCounts[u.status]++; });

    return (
      <div data-testid="development-detail">
        {quoteFlowContext?.opportunity_id && (
          <div className="mb-4 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-3" data-testid="map-quote-flow-banner">
            <p className="text-xs text-cyan-200 font-semibold">Flujo forzado de Cotización (Desarrollos)</p>
            <p className="text-[11px] text-cyan-100 mt-1">
              Lead: {quoteFlowContext?.lead?.name || 'N/D'} · Oportunidad: {quoteFlowContext?.opportunity?.title || quoteFlowContext.opportunity_id}
            </p>
            <p className="text-[10px] text-cyan-100/80 mt-1">Selecciona unidad en el mapa 2D, genera y envía la cotización para mover etapa.</p>
          </div>
        )}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="text-muted-foreground hover:text-white" data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Volver</Button>
          <div className="flex-1">
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">{selected.name}</h2>
            <p className="text-xs text-muted-foreground">{selected.address}{selected.city && `, ${selected.city}`}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => loadQuoteHistory(selected.id)} className="text-xs border-white/10 hover:bg-white/5" data-testid="quotes-history-btn"><History className="w-3 h-3 mr-1" />Cotizaciones</Button>
            {isAdmin && <Button variant="outline" size="sm" onClick={() => { setFichaForm({ name: selected.name, description: selected.description, address: selected.address, city: selected.city, state: selected.state, gallery_images: selected.gallery_images || [], brochure_url: selected.brochure_url || '', video_links: selected.video_links || [], virtual_tour_url: selected.virtual_tour_url || '', waze_url: selected.waze_url || '', google_maps_url: selected.google_maps_url || '', cover_image_url: selected.cover_image_url || '' }); setEditFicha(true); }} className="text-xs border-violet-500/20 text-violet-400 hover:bg-violet-500/10" data-testid="edit-ficha-btn"><Edit3 className="w-3 h-3 mr-1" />Editar Ficha</Button>}
          </div>
        </div>

        {isAdmin && (
          <Card className="mb-6 bg-black/20 border-white/5" data-testid="dev-campaigns-card">
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-white mb-3">Campanas de este desarrollo</p>
              <CampaignPanel scopeType="development" scopeId={selected.id} scopeName={selected.name} scopeLabel="desarrollo" />
            </CardContent>
          </Card>
        )}
        {/* Cover Image + Gallery */}
        {selected.cover_image_url && (
          <div className="mb-4 rounded-lg overflow-hidden h-48 sm:h-64">
            <img src={resolveUrl(selected.cover_image_url)} alt={selected.name} className="w-full h-full object-cover" data-testid="dev-cover-image" />
          </div>
        )}
        {selected.gallery_images?.length > 0 && (
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2" data-testid="gallery-strip">
            {selected.gallery_images.map((url, i) => (
              <img key={i} src={resolveUrl(url)} alt={`Galeria ${i + 1}`} className="h-20 w-28 rounded-md object-cover shrink-0 border border-white/5 hover:border-violet-500/30 transition-colors cursor-pointer" onClick={() => window.open(resolveUrl(url), '_blank')} />
            ))}
          </div>
        )}

        {/* Quick Links Row */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {selected.brochure_url && <a href={resolveUrl(selected.brochure_url)} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-400 text-[10px] font-medium border border-blue-500/20 hover:bg-blue-500/20 transition-colors" data-testid="brochure-link"><FileText className="w-3 h-3" />Brochure</a>}
          {selected.video_links?.map((v, i) => <a key={i} href={v} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-500/10 text-red-400 text-[10px] font-medium border border-red-500/20 hover:bg-red-500/20 transition-colors"><Video className="w-3 h-3" />Video {i + 1}</a>)}
          {selected.virtual_tour_url && <a href={selected.virtual_tour_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-medium border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors" data-testid="virtual-tour-link"><Globe className="w-3 h-3" />Recorrido Virtual</a>}
          {selected.waze_url && <a href={selected.waze_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-cyan-500/10 text-cyan-400 text-[10px] font-medium border border-cyan-500/20 hover:bg-cyan-500/20 transition-colors" data-testid="waze-link"><Navigation className="w-3 h-3" />Waze</a>}
          {selected.google_maps_url && <a href={selected.google_maps_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-500/10 text-green-400 text-[10px] font-medium border border-green-500/20 hover:bg-green-500/20 transition-colors" data-testid="maps-link"><MapPin className="w-3 h-3" />Google Maps</a>}
        </div>

        {/* Status Legend */}
        <div className="flex gap-3 mb-4 flex-wrap">
          {Object.entries(statusCounts).map(([s, c]) => (
            <div key={s} className="flex items-center gap-1.5"><div className={`w-3 h-3 rounded-sm ${STATUS_COLORS[s]?.split(' ')[0]}`} /><span className="text-[10px] text-muted-foreground">{STATUS_LABELS[s]}: <strong className="text-white">{c}</strong></span></div>
          ))}
        </div>

        {/* 2D Building Grid */}
        <Card className="bg-card/50 backdrop-blur-md border-white/5 mb-6" data-testid="building-map-2d">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Compass className="w-4 h-4 text-blue-400" />Mapa de Disponibilidad 2D</h3>
              <div className="flex gap-1 text-[9px] text-muted-foreground">
                {(selected.orientations || []).map(o => <span key={o} className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">{ORI_SHORT[o] || o}</span>)}
              </div>
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-fit">
                <div className="flex items-center gap-1.5 mb-1 pl-12">
                  {Array.from({ length: unitsPerFloor }, (_, i) => {
                    const ori = (selected.orientations || [])[i % (selected.orientations || []).length];
                    return <div key={i} className="w-16 text-center"><span className="text-[8px] text-blue-400 font-mono">{ORI_SHORT[ori] || ''}</span></div>;
                  })}
                </div>
                {Array.from({ length: maxFloor }, (_, fi) => {
                  const floor = maxFloor - fi;
                  const floorUnits = units.filter(u => u.floor === floor).sort((a, b) => a.unit_number - b.unit_number);
                  return (
                    <div key={floor} className="flex items-center gap-1.5 mb-1.5">
                      <div className="w-10 text-right"><span className="text-[10px] text-muted-foreground font-mono">P{floor}</span></div>
                      {floorUnits.map(u => (
                        <button key={u.id} onClick={() => setSelectedUnit({ ...u })} className={`w-16 h-12 rounded-md ${STATUS_COLORS[u.status]} text-white transition-all transform hover:scale-105 border border-white/10 flex flex-col items-center justify-center`} data-testid={`unit-${u.label}`} title={`${u.label} - ${STATUS_LABELS[u.status]} - $${u.price?.toLocaleString()}`}>
                          <span className="text-[10px] font-bold leading-tight">{u.label}</span>
                          <span className="text-[7px] opacity-80">{u.price ? `$${(u.price / 1000).toFixed(0)}k` : ''}</span>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* UNIT DETAIL DIALOG */}
        <Dialog open={!!selectedUnit} onOpenChange={() => setSelectedUnit(null)}>
          <DialogContent className="max-w-lg bg-[#0a0c12] border-white/[0.06] max-h-[90vh] overflow-y-auto" data-testid="unit-detail-dialog">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">{selectedUnit?.label}</DialogTitle>
              <DialogDescription className="text-muted-foreground text-xs">Piso {selectedUnit?.floor} - Editar detalles de la unidad</DialogDescription>
            </DialogHeader>
            {selectedUnit && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-white/60">Numero Depto</Label><Input value={selectedUnit.label || ''} onChange={e => setSelectedUnit({ ...selectedUnit, label: e.target.value })} className="bg-black/20 border-white/10 text-xs" data-testid="unit-label-input" /></div>
                  <div><Label className="text-xs text-white/60">Status</Label>
                    <Select value={selectedUnit.status} onValueChange={v => setSelectedUnit({ ...selectedUnit, status: v })}><SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="unit-status-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-white/60">Precio</Label><Input type="number" value={selectedUnit.price || ''} onChange={e => setSelectedUnit({ ...selectedUnit, price: parseFloat(e.target.value) || 0 })} className="bg-black/20 border-white/10 text-xs" data-testid="unit-price" /></div>
                  <div><Label className="text-xs text-white/60">Orientacion</Label>
                    <Select value={selectedUnit.orientation || ''} onValueChange={v => setSelectedUnit({ ...selectedUnit, orientation: v })}><SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="unit-orientation-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger><SelectContent>{ORIENTATIONS.map(o => <SelectItem key={o} value={o}>{ORI_SHORT[o]} - {o.charAt(0).toUpperCase() + o.slice(1)}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[['m2', 'area_m2'], ['Recamaras', 'bedrooms'], ['Banos', 'bathrooms'], ['Estacionamientos', 'parking_spots']].map(([l, k]) => (
                    <div key={k}><Label className="text-[10px] text-white/60">{l}</Label><Input type="number" value={selectedUnit[k] ?? ''} onChange={e => setSelectedUnit({ ...selectedUnit, [k]: k === 'area_m2' ? parseFloat(e.target.value) || 0 : parseInt(e.target.value) || 0 })} className="bg-black/20 border-white/10 text-xs h-8" data-testid={`unit-${k}`} /></div>
                  ))}
                </div>
                {/* Amenities */}
                <div>
                  <Label className="text-xs text-white/60 mb-2 block">Amenidades / Gadgets</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                    {Object.keys(AMENITY_NAMES).map(key => {
                      const Icon = AMENITY_ICONS[key] || Package;
                      const active = (selectedUnit.amenities || []).includes(key);
                      return <button key={key} type="button" onClick={() => toggleUnitAmenity(key)} className={`flex items-center gap-1 px-2 py-1.5 rounded text-[9px] font-medium border transition-all ${active ? 'bg-violet-500/15 text-violet-400 border-violet-500/30' : 'bg-black/20 text-white/40 border-white/5 hover:border-white/10'}`} data-testid={`amenity-${key}`}><Icon className="w-3 h-3 shrink-0" />{AMENITY_NAMES[key]}</button>;
                    })}
                  </div>
                  {(selectedUnit.amenities || []).length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-[10px] text-white/40">Costo adicional por amenidad:</p>
                      {(selectedUnit.amenities || []).map(key => (
                        <div key={key} className="flex items-center gap-2"><span className="text-[10px] text-violet-400 w-28 truncate">{AMENITY_NAMES[key]}</span><Input type="number" placeholder="0" value={(selectedUnit.amenity_costs || {})[key] || ''} onChange={e => setSelectedUnit(u => ({ ...u, amenity_costs: { ...(u.amenity_costs || {}), [key]: parseFloat(e.target.value) || 0 } }))} className="bg-black/20 border-white/10 text-xs h-7 w-28" /></div>
                      ))}
                    </div>
                  )}
                </div>
                <div><Label className="text-xs text-white/60">Amenidad Adicional (personalizada)</Label><Input value={selectedUnit.custom_amenity || ''} onChange={e => setSelectedUnit({ ...selectedUnit, custom_amenity: e.target.value })} placeholder="Ej: Helipuerto, Cancha de padel..." className="bg-black/20 border-white/10 text-xs" data-testid="unit-custom-amenity" /></div>
                {/* Floor Plan */}
                <div>
                  <Label className="text-xs text-white/60 mb-1 block">Planta Tipo / Plano (PDF o Imagen)</Label>
                  <input ref={unitPlanRef} type="file" accept=".pdf,image/*" className="hidden" onChange={uploadUnitFloorPlan} />
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => unitPlanRef.current?.click()} disabled={unitFloorPlanUploading} className="text-xs border-white/10 hover:bg-white/5" data-testid="upload-floor-plan-btn">{unitFloorPlanUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}{selectedUnit.floor_plan_url ? 'Cambiar' : 'Subir'} Plano</Button>
                    {selectedUnit.floor_plan_url && <a href={resolveUrl(selectedUnit.floor_plan_url)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300"><FileText className="w-3 h-3" />Ver plano</a>}
                  </div>
                </div>
                <div><Label className="text-xs text-white/60">Notas</Label><Textarea value={selectedUnit.notes || ''} onChange={e => setSelectedUnit({ ...selectedUnit, notes: e.target.value })} rows={2} className="bg-black/20 border-white/10 text-xs" /></div>
                <div className="flex gap-2">
                  <Button onClick={() => updateUnit(selectedUnit.id, { status: selectedUnit.status, price: selectedUnit.price, area_m2: selectedUnit.area_m2, bedrooms: selectedUnit.bedrooms, bathrooms: selectedUnit.bathrooms, notes: selectedUnit.notes, orientation: selectedUnit.orientation, label: selectedUnit.label, parking_spots: selectedUnit.parking_spots, amenities: selectedUnit.amenities || [], amenity_costs: selectedUnit.amenity_costs || {}, custom_amenity: selectedUnit.custom_amenity || '', floor_plan_url: selectedUnit.floor_plan_url || '' })} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid="save-unit-btn">Guardar Unidad</Button>
                  <Button variant="outline" onClick={() => openQuote(selectedUnit)} className="text-xs border-blue-500/20 text-blue-400 hover:bg-blue-500/10" data-testid="generate-quote-btn"><Calculator className="w-3 h-3 mr-1" />Cotizar</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* QUOTE GENERATOR DIALOG */}
        <Dialog open={quoteDialog && !quoteResult} onOpenChange={setQuoteDialog}>
          <DialogContent className="max-w-md bg-[#0a0c12] border-white/[0.06] max-h-[85vh] overflow-y-auto" data-testid="quote-dialog">
            <DialogHeader>
              <DialogTitle className="text-white font-heading flex items-center gap-2"><Calculator className="w-5 h-5 text-blue-400" />Cotizador</DialogTitle>
              <DialogDescription className="text-muted-foreground text-xs">{quoteFlowContext?.opportunity_id ? `Cotización y envío para ${quoteUnit?.label}` : `Cotización simulada para ${quoteUnit?.label}`}</DialogDescription>
            </DialogHeader>
            {quoteUnit && (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Precio base</span><span className="text-white font-medium">${quoteUnit.price?.toLocaleString()}</span></div>
                  {(quoteUnit.amenities || []).filter(a => (quoteUnit.amenity_costs || {})[a] > 0).map(a => (
                    <div key={a} className="flex justify-between text-xs"><span className="text-muted-foreground pl-2">+ {AMENITY_NAMES[a]}</span><span className="text-violet-400">${((quoteUnit.amenity_costs || {})[a] || 0).toLocaleString()}</span></div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-white/60">Enganche (%)</Label><Input type="number" min={0} max={100} value={quoteForm.down_payment_pct} onChange={e => setQuoteForm({ ...quoteForm, down_payment_pct: parseFloat(e.target.value) || 0 })} className="bg-black/20 border-white/10 text-xs" data-testid="quote-down-pct" /></div>
                  <div><Label className="text-xs text-white/60">Tasa Interes Anual (%)</Label><Input type="number" min={0} max={50} step={0.5} value={quoteForm.annual_interest_rate} onChange={e => setQuoteForm({ ...quoteForm, annual_interest_rate: parseFloat(e.target.value) || 0 })} className="bg-black/20 border-white/10 text-xs" data-testid="quote-rate" /></div>
                </div>
                <div><Label className="text-xs text-white/60">Plazo (meses)</Label>
                  <Select value={String(quoteForm.term_months)} onValueChange={v => setQuoteForm({ ...quoteForm, term_months: parseInt(v) })}><SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="quote-term-months"><SelectValue /></SelectTrigger><SelectContent>{[60, 120, 180, 240, 300, 360].map(m => <SelectItem key={m} value={String(m)}>{m} meses ({m / 12} anos)</SelectItem>)}</SelectContent></Select>
                </div>
                {/* Notarial Costs */}
                <div className="p-3 rounded-lg bg-red-500/[0.03] border border-red-500/10">
                  <Label className="text-xs text-red-400 font-semibold mb-2 block">Gastos Notariales</Label>
                  <Select value={quoteForm.notarial_state} onValueChange={v => setQuoteForm({ ...quoteForm, notarial_state: v })}>
                    <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="quote-notarial-state"><SelectValue placeholder="Seleccionar estado" /></SelectTrigger>
                    <SelectContent>{MX_STATES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  {notarialInfo && (
                    <div className="mt-2 space-y-1 text-[10px]">
                      <div className="flex justify-between"><span className="text-muted-foreground">ISAI</span><span className="text-red-400">{notarialInfo.isai}%</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Honorarios Notariales</span><span className="text-red-400">{notarialInfo.notario}%</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Registro Publico</span><span className="text-red-400">{notarialInfo.registro}%</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Avaluo</span><span className="text-red-400">{notarialInfo.avaluo}%</span></div>
                    </div>
                  )}
                </div>
                {quoteFlowContext?.opportunity_id && (
                  <div className="p-3 rounded-lg border border-cyan-500/20 bg-cyan-500/5 space-y-2" data-testid="quote-delivery-section">
                    <p className="text-[11px] text-cyan-200 font-semibold">Entrega obligatoria antes de mover etapa</p>
                    <div className="flex items-center gap-4 text-[11px] text-white/80">
                      <label className="flex items-center gap-1" data-testid="quote-send-whatsapp-toggle-wrap">
                        <input type="checkbox" checked={quoteDelivery.send_whatsapp} onChange={(e) => setQuoteDelivery(prev => ({ ...prev, send_whatsapp: e.target.checked }))} data-testid="quote-send-whatsapp-toggle" />
                        WhatsApp
                      </label>
                      <label className="flex items-center gap-1" data-testid="quote-send-email-toggle-wrap">
                        <input type="checkbox" checked={quoteDelivery.send_email} onChange={(e) => setQuoteDelivery(prev => ({ ...prev, send_email: e.target.checked }))} data-testid="quote-send-email-toggle" />
                        Email
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <PhoneInput data-testid="quote-recipient-phone" value={quoteDelivery.recipient_phone} onChange={v => setQuoteDelivery(prev => ({ ...prev, recipient_phone: v }))} />
                      <Input value={quoteDelivery.recipient_email} onChange={(e) => setQuoteDelivery(prev => ({ ...prev, recipient_email: e.target.value }))} placeholder="Email destinatario" className="bg-black/20 border-white/10 text-xs" data-testid="quote-recipient-email" />
                    </div>
                  </div>
                )}
                <Button onClick={generateQuote} disabled={quoteGenerating} className="w-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold" data-testid="submit-quote-btn">
                  {quoteGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}{quoteFlowContext?.opportunity_id ? 'Enviar Cotización y mover etapa' : 'Generar Cotización Simulada'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* EDIT FICHA DIALOG */}
        <Dialog open={editFicha} onOpenChange={setEditFicha}>
          <DialogContent className="max-w-lg bg-[#0a0c12] border-white/[0.06] max-h-[85vh] overflow-y-auto" data-testid="edit-ficha-dialog">
            <DialogHeader>
              <DialogTitle className="text-white font-heading flex items-center gap-2"><Edit3 className="w-5 h-5 text-violet-400" />Editar Ficha del Desarrollo</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">Galeria, brochure, videos, recorrido virtual y ubicacion</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Gallery */}
              <div>
                <Label className="text-xs text-white/60 mb-2 block flex items-center gap-1"><Image className="w-3 h-3" />Galeria de Imagenes</Label>
                <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={uploadGalleryImage} />
                <div className="flex gap-2 flex-wrap mb-2">
                  {(fichaForm.gallery_images || []).map((url, i) => (
                    <div key={i} className="relative group">
                      <img src={resolveUrl(url)} alt={`Img ${i + 1}`} className="w-20 h-16 rounded object-cover border border-white/5" />
                      <button onClick={() => setFichaForm(f => ({ ...f, gallery_images: f.gallery_images.filter((_, j) => j !== i) }))} className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-2.5 h-2.5 text-white" /></button>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={() => galleryRef.current?.click()} disabled={galleryUploading} className="text-xs border-white/10" data-testid="add-gallery-btn">{galleryUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}Agregar Imagenes</Button>
              </div>
              {/* Brochure */}
              <div>
                <Label className="text-xs text-white/60 mb-1 block flex items-center gap-1"><FileText className="w-3 h-3" />Brochure (PDF)</Label>
                <input ref={brochureRef} type="file" accept=".pdf" className="hidden" onChange={uploadBrochure} />
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => brochureRef.current?.click()} disabled={brochureUploading} className="text-xs border-white/10" data-testid="upload-brochure-btn">{brochureUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}{fichaForm.brochure_url ? 'Cambiar' : 'Subir'} Brochure</Button>
                  {fichaForm.brochure_url && <a href={resolveUrl(fichaForm.brochure_url)} target="_blank" rel="noreferrer" className="text-[10px] text-blue-400">Ver PDF</a>}
                </div>
              </div>
              {/* Video Links */}
              <div>
                <Label className="text-xs text-white/60 mb-1 block flex items-center gap-1"><Video className="w-3 h-3" />Links de Videos</Label>
                {(fichaForm.video_links || []).map((v, i) => (
                  <div key={i} className="flex gap-2 mb-1">
                    <Input value={v} onChange={e => setFichaForm(f => ({ ...f, video_links: f.video_links.map((x, j) => j === i ? e.target.value : x) }))} placeholder="https://youtube.com/..." className="bg-black/20 border-white/10 text-xs flex-1" />
                    <Button variant="ghost" size="sm" onClick={() => setFichaForm(f => ({ ...f, video_links: f.video_links.filter((_, j) => j !== i) }))} className="text-destructive"><X className="w-3 h-3" /></Button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={() => setFichaForm(f => ({ ...f, video_links: [...(f.video_links || []), ''] }))} className="text-xs text-blue-400"><Plus className="w-3 h-3 mr-1" />Agregar Video</Button>
              </div>
              {/* Virtual Tour */}
              <div>
                <Label className="text-xs text-white/60 flex items-center gap-1"><Globe className="w-3 h-3" />Link Recorrido Virtual</Label>
                <Input value={fichaForm.virtual_tour_url || ''} onChange={e => setFichaForm(f => ({ ...f, virtual_tour_url: e.target.value }))} placeholder="https://my360tour.com/..." className="bg-black/20 border-white/10 text-xs" data-testid="ficha-virtual-tour" />
              </div>
              {/* Waze & Google Maps */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-white/60 flex items-center gap-1"><Navigation className="w-3 h-3" />Link Waze</Label>
                  <Input value={fichaForm.waze_url || ''} onChange={e => setFichaForm(f => ({ ...f, waze_url: e.target.value }))} placeholder="https://waze.com/ul/..." className="bg-black/20 border-white/10 text-xs" data-testid="ficha-waze" />
                </div>
                <div>
                  <Label className="text-xs text-white/60 flex items-center gap-1"><MapPin className="w-3 h-3" />Link Google Maps</Label>
                  <Input value={fichaForm.google_maps_url || ''} onChange={e => setFichaForm(f => ({ ...f, google_maps_url: e.target.value }))} placeholder="https://maps.google.com/..." className="bg-black/20 border-white/10 text-xs" data-testid="ficha-maps" />
                </div>
              </div>
              <Button onClick={saveFicha} className="w-full bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold" data-testid="save-ficha-btn"><Edit3 className="w-4 h-4 mr-2" />Guardar Ficha</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* QUOTES HISTORY DIALOG */}
        <Dialog open={showHistory} onOpenChange={setShowHistory}>
          <DialogContent className="max-w-2xl bg-[#0a0c12] border-white/[0.06] max-h-[85vh] overflow-y-auto" data-testid="quotes-history-dialog">
            <DialogHeader>
              <DialogTitle className="text-white font-heading flex items-center gap-2"><History className="w-5 h-5 text-blue-400" />Historial de Cotizaciones</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">{quotesHistory.length} cotizacion(es)</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {quotesHistory.map(q => (
                <div key={q.id} className="p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors" data-testid={`quote-item-${q.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-white">{q.unit_label}</span>
                      <Badge variant="outline" className="text-amber-400 border-amber-500/20 text-[8px]">SIM</Badge>
                    </div>
                    <span className="text-[9px] text-muted-foreground">{new Date(q.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-[10px] mb-2">
                    <div><span className="text-muted-foreground">Total: </span><span className="text-white font-medium">${q.total_price?.toLocaleString()}</span></div>
                    <div><span className="text-muted-foreground">Enganche: </span><span className="text-white">{q.down_payment_pct}%</span></div>
                    <div><span className="text-muted-foreground">Tasa: </span><span className="text-white">{q.annual_interest_rate}%</span></div>
                    <div><span className="text-muted-foreground">Mensual: </span><span className="text-emerald-400 font-medium">${q.monthly_payment?.toLocaleString()}</span></div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button variant="ghost" size="sm" onClick={() => { setQuoteResult(q); setShowHistory(false); }} className="text-[10px] h-6 text-blue-400 hover:bg-blue-500/10"><Eye className="w-3 h-3 mr-1" />Ver</Button>
                    <Button variant="ghost" size="sm" onClick={() => { const email = prompt('Email del destinatario:'); if (email) resendQuote(q.id, email); }} className="text-[10px] h-6 text-emerald-400 hover:bg-emerald-500/10"><Send className="w-3 h-3 mr-1" />Enviar</Button>
                  </div>
                </div>
              ))}
              {quotesHistory.length === 0 && <p className="text-center text-xs text-muted-foreground py-6">No hay cotizaciones para este desarrollo</p>}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── DEVELOPMENTS LIST ───
  return (
    <div data-testid="developments-page">
      {quoteFlowContext?.opportunity_id && (
        <div className="mb-4 rounded-xl border border-cyan-500/20 bg-cyan-500/10 px-4 py-3" data-testid="map-quote-select-development-banner">
          <p className="text-xs text-cyan-200 font-semibold">Selecciona un desarrollo para continuar la cotización</p>
          <p className="text-[11px] text-cyan-100/90 mt-1">Oportunidad: {quoteFlowContext?.opportunity?.title || quoteFlowContext.opportunity_id}</p>
        </div>
      )}
      <div className="flex items-center justify-between mb-6 sm:mb-8 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">Desarrollos Inmobiliarios</h2>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">{devs.length} desarrollo{devs.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-violet-600 hover:bg-violet-500 shadow-lg shadow-violet-600/20 font-semibold text-xs sm:text-sm" data-testid="add-development-btn"><Plus className="w-4 h-4 mr-1 sm:mr-2" />Nuevo Desarrollo</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : devs.length === 0 ? (
        <Card className="bg-card/50 backdrop-blur-md border-white/5"><CardContent className="p-12 text-center">
          <Layers className="w-12 h-12 text-violet-400/30 mx-auto mb-3" /><p className="text-muted-foreground text-sm">No tienes desarrollos registrados</p>
          <Button onClick={() => setShowForm(true)} className="mt-4 bg-violet-600 hover:bg-violet-500 text-xs" data-testid="empty-add-btn"><Plus className="w-4 h-4 mr-1" />Crear Primer Desarrollo</Button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {devs.map(d => (
            <Card key={d.id} className="bg-card/50 backdrop-blur-md border-white/5 hover:border-violet-500/20 transition-colors cursor-pointer group overflow-hidden" data-testid={`dev-card-${d.id}`}>
              {d.cover_image_url && <div className="h-32 overflow-hidden"><img src={resolveUrl(d.cover_image_url)} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /></div>}
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {!d.cover_image_url && <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-violet-400" /></div>}
                    <div><h3 className="text-sm font-semibold text-white group-hover:text-violet-300 transition-colors">{d.name}</h3><CopyableId id={d.id} /><p className="text-[10px] text-muted-foreground">{d.type === 'edificio' ? 'Edificio' : d.type === 'fraccionamiento' ? 'Fraccionamiento' : 'Mixto'}</p></div>
                  </div>
                  <Badge variant="outline" className={`text-[9px] ${d.is_active ? 'text-emerald-400 border-emerald-500/20' : 'text-red-400 border-red-500/20'}`}>{d.is_active ? 'Activo' : 'Inactivo'}</Badge>
                </div>
                {d.address && <p className="text-[10px] text-muted-foreground flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" />{d.address}{d.city ? `, ${d.city}` : ''}</p>}
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[['Total', d.units_total, 'text-white', 'bg-white/[0.02]'], ['Disponible', d.units_available, 'text-emerald-400', 'bg-emerald-500/5'], ['Apartado', d.units_reserved, 'text-amber-400', 'bg-amber-500/5'], ['Vendido', d.units_sold, 'text-red-400', 'bg-red-500/5']].map(([l, v, tc, bg]) => (
                    <div key={l} className={`text-center p-1.5 rounded ${bg}`}><p className={`text-lg font-bold ${tc}`}>{v}</p><p className={`text-[8px] ${tc}/60`}>{l}</p></div>
                  ))}
                </div>
                <div className="h-2 rounded-full bg-black/30 overflow-hidden mb-3"><div className="h-full flex">{d.units_total > 0 && <><div className="bg-red-500" style={{ width: `${(d.units_sold / d.units_total) * 100}%` }} /><div className="bg-amber-500" style={{ width: `${(d.units_reserved / d.units_total) * 100}%` }} /><div className="bg-emerald-500" style={{ width: `${(d.units_available / d.units_total) * 100}%` }} /></>}</div></div>
                <div className="flex gap-1.5">
                  <Button variant="ghost" size="sm" onClick={() => openDev(d.id)} className="flex-1 text-xs hover:bg-violet-500/10 hover:text-violet-400" data-testid={`view-dev-${d.id}`}><Eye className="w-3 h-3 mr-1" />Ver Mapa</Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(d.id)} className="text-xs text-destructive hover:bg-destructive/10" data-testid={`delete-dev-${d.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* CREATE DEVELOPMENT DIALOG */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg bg-[#0a0c12] border-white/[0.06] max-h-[85vh] overflow-y-auto" data-testid="create-dev-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2"><Layers className="w-5 h-5 text-violet-400" />Nuevo Desarrollo</DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">Configura el desarrollo y se generaran las unidades automaticamente</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Cover Upload */}
            <div>
              <Label className="text-xs text-white/60 mb-1 block">Imagen Principal</Label>
              <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={uploadCover} />
              {coverPreview ? (
                <div className="relative h-32 rounded-lg overflow-hidden mb-2"><img src={resolveUrl(coverPreview)} alt="Cover" className="w-full h-full object-cover" /><button onClick={() => { setCoverPreview(''); setForm(f => ({ ...f, cover_image_url: '' })); }} className="absolute top-2 right-2 p-1 rounded bg-black/60 text-white hover:bg-red-500/80"><X className="w-3 h-3" /></button></div>
              ) : (
                <button onClick={() => coverRef.current?.click()} disabled={coverUploading} className="w-full h-24 rounded-lg border-2 border-dashed border-white/10 hover:border-violet-500/30 flex flex-col items-center justify-center gap-1 transition-colors" data-testid="upload-cover-btn">{coverUploading ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : <Upload className="w-5 h-5 text-muted-foreground" />}<span className="text-[10px] text-muted-foreground">{coverUploading ? 'Subiendo...' : 'Subir imagen de portada'}</span></button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs text-white/60">Nombre *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Torre Skyline" className="bg-black/20 border-white/10" data-testid="dev-name" /></div>
              <div><Label className="text-xs text-white/60">Tipo</Label><Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}><SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="dev-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="edificio">Edificio</SelectItem><SelectItem value="fraccionamiento">Fraccionamiento</SelectItem><SelectItem value="mixto">Mixto</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs text-white/60">Moneda</Label><Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}><SelectTrigger className="bg-black/20 border-white/10 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent></Select></div>
            </div>
            <div><Label className="text-xs text-white/60">Descripcion</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="bg-black/20 border-white/10 text-xs" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-white/60">Direccion</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="bg-black/20 border-white/10 text-xs" /></div>
              <div><Label className="text-xs text-white/60">Ciudad</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="bg-black/20 border-white/10 text-xs" /></div>
              <div><Label className="text-xs text-white/60">Estado</Label><Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} className="bg-black/20 border-white/10 text-xs" /></div>
            </div>
            <div className="p-3 rounded-lg bg-violet-500/[0.03] border border-violet-500/10">
              <p className="text-xs font-semibold text-violet-400 mb-3 flex items-center gap-1.5"><Compass className="w-3.5 h-3.5" />Configuracion del Edificio</p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div><Label className="text-[10px] text-white/60">Pisos</Label><Input type="number" min={1} max={100} value={form.floors} onChange={e => setForm({ ...form, floors: parseInt(e.target.value) || 1 })} className="bg-black/20 border-white/10 text-xs" data-testid="dev-floors" /></div>
                <div><Label className="text-[10px] text-white/60">Unidades/Piso</Label><Input type="number" min={1} max={50} value={form.units_per_floor} onChange={e => setForm({ ...form, units_per_floor: parseInt(e.target.value) || 1 })} className="bg-black/20 border-white/10 text-xs" data-testid="dev-units-per-floor" /></div>
              </div>
              <div className="mb-3"><Label className="text-[10px] text-white/60 mb-1.5 block">Orientaciones</Label><div className="flex gap-1.5 flex-wrap">{ORIENTATIONS.map(o => <button key={o} type="button" onClick={() => toggleOrientation(o)} className={`px-2 py-1 rounded text-[10px] font-medium border transition-all ${form.orientations.includes(o) ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-black/20 text-white/30 border-white/5'}`} data-testid={`ori-${o}`}>{ORI_SHORT[o]}</button>)}</div></div>
              <div className="p-2 rounded bg-black/30 border border-white/5"><p className="text-[9px] text-muted-foreground">{form.floors} pisos x {form.units_per_floor} unidades = {form.floors * form.units_per_floor} unidades</p></div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[['Precio Base', 'base_price'], ['m2', 'area_m2'], ['Recamaras', 'bedrooms'], ['Banos', 'bathrooms']].map(([l, k]) => (
                <div key={k}><Label className="text-[10px] text-white/60">{l}</Label><Input type="number" value={form[k] || ''} onChange={e => setForm({ ...form, [k]: k === 'area_m2' || k === 'base_price' ? parseFloat(e.target.value) || 0 : parseInt(e.target.value) || 0 })} className="bg-black/20 border-white/10 text-xs" data-testid={`dev-${k}`} /></div>
              ))}
            </div>
            <Button onClick={create} disabled={saving} className="w-full bg-violet-600 hover:bg-violet-500 text-white font-semibold" data-testid="submit-dev-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Layers className="w-4 h-4 mr-2" />}{saving ? 'Creando...' : `Crear Desarrollo (${form.floors * form.units_per_floor} unidades)`}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
